import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiBell, FiX, FiCheck, FiCheckCircle, FiVolume2, FiVolumeX,
    FiEye, FiTrash2, FiExternalLink, FiCalendar, FiMessageSquare,
    FiAlertTriangle, FiFileText, FiCheckSquare, FiChevronDown, FiChevronUp, FiArrowRight
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import {
    getNotifications, markNotificationAsRead, markAllNotificationsAsRead,
    deleteNotification, NOTIFICATION_TYPES
} from '../../lib/supabase/notifications';
import notificationSound from '../../lib/soundUtils/notificationSound';
import Toast from '../common/Toast';

const NotificationMenu = ({ isOpen, onClose, onRefreshBadge, isSidebarOpen, latestNotification, onSoundSettingsChange }) => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState([]);
    const [showingOld, setShowingOld] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [expandedNotificationId, setExpandedNotificationId] = useState(null);
    const menuRef = useRef(null);

    // Show toast notification based on type
    const showToastForNotification = (notification) => {
        // Check if notification is assignment-related
        const isAssignmentNotification = [
            NOTIFICATION_TYPES.ASSIGNMENT_CREATED,
            NOTIFICATION_TYPES.ASSIGNMENT_UPDATED,
            NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
            NOTIFICATION_TYPES.ASSIGNMENT_COMPLETED,
            NOTIFICATION_TYPES.ASSIGNMENT_DUE_SOON
        ].includes(notification.type);

        if (isAssignmentNotification && notification.action_url) {
            // Custom toast for assignment notifications with navigation button
            Toast.custom(
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1">{notification.title}</h4>
                        {notification.message && (
                            <p className="text-sm text-gray-700 mb-3">{notification.message}</p>
                        )}
                        <button
                            onClick={() => {
                                navigate(notification.action_url);
                                markNotificationAsRead(notification.id);
                                Toast.dismiss();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            <span>View Assignment</span>
                            <FiArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>,
                {
                    position: 'top-right',
                    autoClose: false,
                    closeOnClick: false
                }
            );
        } else {
            // Standard toast for other notification types
            const toastType = getToastTypeForNotification(notification.type);
            Toast[toastType](notification.message || notification.title, {
                title: notification.title,
                position: 'top-right',
                autoClose: 5000
            });
        }
    };

    // Map notification type to toast type
    const getToastTypeForNotification = (notificationType) => {
        switch (notificationType) {
            case NOTIFICATION_TYPES.ASSIGNMENT_COMPLETED:
            case NOTIFICATION_TYPES.PAYMENT_SUCCESS:
                return 'success';
            case NOTIFICATION_TYPES.SYSTEM_ALERT:
            case NOTIFICATION_TYPES.PAYMENT_FAILED:
                return 'error';
            case NOTIFICATION_TYPES.ASSIGNMENT_DUE_SOON:
                return 'warning';
            case NOTIFICATION_TYPES.ASSIGNMENT_CREATED:
            case NOTIFICATION_TYPES.ASSIGNMENT_UPDATED:
            case NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED:
            case NOTIFICATION_TYPES.MESSAGE_RECEIVED:
            case NOTIFICATION_TYPES.USER_MENTIONED:
            case NOTIFICATION_TYPES.SYSTEM_UPDATE:
            default:
                return 'info';
        }
    };

    useEffect(() => {
        const savedVolume = localStorage.getItem('notificationVolume');
        const savedMuted = localStorage.getItem('notificationMuted');
        if (savedVolume) setVolume(parseFloat(savedVolume));
        if (savedMuted) setIsMuted(savedMuted === 'true');
    }, []);

    useEffect(() => {
        if (isOpen && userProfile) {
            fetchNotifications();
            // Refresh badge when opening menu
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        }
    }, [isOpen, userProfile, showingOld, onRefreshBadge]);

    // When a new notification arrives (signalled by the hook via latestNotification),
    // refresh the list if the menu is open so it appears instantly without needing a click.
    useEffect(() => {
        if (!latestNotification) return;
        fetchNotifications();
        if (latestNotification.notification) {
            showToastForNotification(latestNotification.notification);
        }
    }, [latestNotification]);

    const fetchNotifications = async () => {
        try {
            if (!userProfile?.id) return;
            const data = await getNotifications(userProfile.id, showingOld);
            setNotifications(data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await markNotificationAsRead(notificationId);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await markAllNotificationsAsRead(userProfile.id);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await deleteNotification(notificationId);
            fetchNotifications();
            if (onRefreshBadge) {
                onRefreshBadge();
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const handleVolumeChange = (newVolume) => {
        setVolume(newVolume);
        localStorage.setItem('notificationVolume', newVolume.toString());
        if (onSoundSettingsChange) onSoundSettingsChange();
    };

    const handleMuteToggle = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem('notificationMuted', newMuted.toString());
        if (onSoundSettingsChange) onSoundSettingsChange();
    };

    const getNotificationIcon = (type) => {
        const iconProps = { className: "w-5 h-5 md:w-4 md:h-4" };
        switch (type) {
            case 'assignment_created':
            case 'assignment_updated':
                return <FiFileText {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-blue-600" />;
            case 'assignment_completed':
                return <FiCheckSquare {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-green-600" />;
            case 'assignment_due_soon':
                return <FiCalendar {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-orange-600" />;
            case 'message_received':
                return <FiMessageSquare {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-purple-600" />;
            case 'system_alert':
                return <FiAlertTriangle {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-red-600" />;
            default:
                return <FiBell {...iconProps} className="w-5 h-5 md:w-4 md:h-4 text-blue-600" />;
        }
    };

    const getPriorityColors = (priority) => {
        switch (priority) {
            case 'urgent':
                return { border: 'border-l-red-500', bg: 'bg-red-50 dark:bg-red-900/10', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
            case 'high':
                return { border: 'border-l-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/10', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
            case 'normal':
                return { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
            case 'low':
                return { border: 'border-l-gray-400', bg: 'bg-gray-50 dark:bg-gray-900/10', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300' };
            default:
                return { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleNotificationClick = (notification) => {
        // Toggle expansion - if clicking the same notification, collapse it; otherwise expand the new one
        if (expandedNotificationId === notification.id) {
            setExpandedNotificationId(null);
        } else {
            setExpandedNotificationId(notification.id);
        }

        // Navigate if there's an action URL
        if (notification.action_url) {
            if (notification?.title?.toLowerCase()?.includes("delivery assignment")) {
                return navigate(`${company?.slug}/my-deliveries`)
            }
            navigate(notification.action_url);
            if (!notification.is_read) {
                handleMarkAsRead(notification.id);
            }
            onClose();
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
            {/* Menu - Responsive: Full screen on mobile, fixed position on desktop */}
            <div
                ref={menuRef}
                className={`
                    fixed z-50 bg-white dark:bg-zinc-800 shadow-2xl overflow-hidden flex flex-col transition-all duration-300
                    md:bottom-4 md:w-[440px] md:h-[600px] md:rounded-xl md:border md:border-gray-200 md:dark:border-zinc-700
                    max-md:inset-0 max-md:w-full max-md:h-full max-md:rounded-none
                `}
                style={{
                    left: window.innerWidth >= 768 ? (isSidebarOpen ? 'calc(16rem + 1rem)' : 'calc(5.5rem + 0.5rem)') : undefined
                }}
            >
                {/* Header - Mobile optimized with larger touch targets */}
                <div className="flex items-center justify-between p-4 md:p-4 border-b border-gray-200 dark:border-zinc-700 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 flex-shrink-0 safe-area-inset-top">
                    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex-shrink-0">
                            <FiBell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-base md:text-sm truncate">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 md:p-2 hover:bg-white/70 dark:hover:bg-zinc-700 rounded-xl transition-colors flex-shrink-0 touch-manipulation">
                        <FiX className="w-5 h-5 md:w-4 md:h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Controls - Mobile optimized */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 p-3 md:p-4 bg-white/70 dark:bg-zinc-900/50 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                    <div className="flex items-center gap-2 md:gap-3">
                        <button
                            onClick={handleMuteToggle}
                            className={`p-2.5 md:p-2 rounded-lg transition-all touch-manipulation ${isMuted
                                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                                }`}
                        >
                            {isMuted ? <FiVolumeX className="w-5 h-5 md:w-4 md:h-4" /> : <FiVolume2 className="w-5 h-5 md:w-4 md:h-4" />}
                        </button>
                        {!isMuted && (
                            <div className="flex items-center gap-2 flex-1 md:flex-initial">
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={volume}
                                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                                    className="flex-1 md:w-20 h-2 bg-gray-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer touch-manipulation"
                                />
                                <button
                                    onClick={() => notificationSound.testSound()}
                                    className="px-3 py-1.5 md:py-1.5 text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 active:bg-blue-300 rounded-lg transition-colors touch-manipulation whitespace-nowrap"
                                >
                                    Test
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 justify-between md:justify-start">
                        <button
                            onClick={() => setShowingOld(!showingOld)}
                            className="flex items-center gap-1.5 px-3 py-2 md:py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-700 active:bg-gray-200 dark:active:bg-zinc-600 rounded-lg transition-colors touch-manipulation"
                        >
                            <FiEye className="w-3.5 h-3.5 md:w-3 md:h-3" />
                            {showingOld ? 'Recent' : 'Show Read'}
                        </button>
                        {unreadCount > 0 && !showingOld && (
                            <button
                                onClick={handleMarkAllAsRead}
                                className="flex items-center gap-1.5 px-3 py-2 md:py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 active:bg-blue-100 dark:active:bg-blue-900/30 rounded-lg transition-colors touch-manipulation"
                            >
                                <FiCheckCircle className="w-3.5 h-3.5 md:w-3 md:h-3" />
                                <span className="hidden sm:inline">Mark All Read</span>
                                <span className="sm:hidden">Read All</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Notifications List - Mobile optimized spacing */}
                <div className="flex-1 overflow-y-auto overscroll-behavior-contain">
                    {notifications.length === 0 ? (
                        <div className="p-16 md:p-12 text-center text-gray-500 dark:text-gray-400">
                            <div className="w-20 h-20 md:w-16 md:h-16 mx-auto mb-4 bg-gray-100 dark:bg-zinc-700 rounded-2xl flex items-center justify-center">
                                <FiBell className="w-8 h-8 md:w-6 md:h-6 text-gray-400" />
                            </div>
                            <p className="text-base md:text-sm font-medium mb-2">No notifications</p>
                            <p className="text-sm md:text-xs text-gray-400 leading-relaxed">
                                {showingOld ? 'No read notifications found' : "You're all caught up! 🎉"}
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-zinc-700">
                            {notifications.map((notification) => {
                                const priorityColors = getPriorityColors(notification.priority);
                                const isExpanded = expandedNotificationId === notification.id;
                                return (
                                    <div
                                        key={notification.id}
                                        className={`group p-4 md:p-4 hover:bg-gray-50/70 dark:hover:bg-zinc-700/50 active:bg-gray-100 dark:active:bg-zinc-600/50 transition-all relative cursor-pointer touch-manipulation ${!notification.is_read ? `border-l-3 ${priorityColors.border} ${priorityColors.bg}` : ''
                                            }`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-3 md:gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="w-8 h-8 md:w-auto md:h-auto flex items-center justify-center md:block">
                                                    {getNotificationIcon(notification.type)}
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-3 md:gap-3">
                                                    <div className="flex-1">
                                                        <h4 className={`text-base md:text-sm font-semibold ${!notification.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                                                            } leading-tight mb-1.5 md:mb-1`}>
                                                            {notification.title}
                                                        </h4>
                                                        {notification.message && (
                                                            <div className="relative">
                                                                <p className={`text-sm md:text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3 ${
                                                                    isExpanded ? '' : 'line-clamp-2'
                                                                }`}>
                                                                    {notification.message}
                                                                </p>
                                                                {/* Show expand/collapse indicator if message is long */}
                                                                {notification.message.length > 100 && (
                                                                    <div className="flex items-center gap-1 text-sm md:text-xs text-blue-500 dark:text-blue-400 font-medium mb-2">
                                                                        {isExpanded ? (
                                                                            <>
                                                                                <FiChevronUp className="w-4 h-4 md:w-3 md:h-3" />
                                                                                <span>Show less</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiChevronDown className="w-4 h-4 md:w-3 md:h-3" />
                                                                                <span>Show more</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm md:text-xs text-gray-400 font-medium">
                                                                    {formatTimestamp(notification.created_at)}
                                                                </span>
                                                                {(notification.priority === 'urgent' || notification.priority === 'high') && (
                                                                    <span className={`px-2.5 py-1 md:px-2 md:py-0.5 text-xs font-medium rounded-full ${priorityColors.badge}`}>
                                                                        {notification.priority === 'urgent' ? 'Urgent' : 'High'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 md:gap-1 max-md:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                                        {!notification.is_read && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMarkAsRead(notification.id);
                                                                }}
                                                                className="p-2.5 md:p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100 dark:hover:bg-blue-900/20 dark:active:bg-blue-900/30 rounded-lg md:rounded-md transition-colors touch-manipulation"
                                                                title="Mark as read"
                                                            >
                                                                <FiCheck className="w-4 h-4 md:w-3 md:h-3" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(notification.id);
                                                            }}
                                                            className="p-2.5 md:p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 active:bg-red-100 dark:hover:bg-red-900/20 dark:active:bg-red-900/30 rounded-lg md:rounded-md transition-colors touch-manipulation"
                                                            title="Delete notification"
                                                        >
                                                            <FiTrash2 className="w-4 h-4 md:w-3 md:h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {!notification.is_read && (
                                            <div className="absolute right-3 md:right-3 top-4 md:top-4 w-2.5 h-2.5 md:w-2 md:h-2 bg-blue-500 rounded-full shadow-sm"></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer - Mobile optimized with safe area */}
                {notifications.length > 0 && (
                    <div className="p-3 md:p-3 pb-safe bg-gray-50/70 dark:bg-zinc-900/50 border-t border-gray-200 dark:border-zinc-700 flex-shrink-0">
                        <div className="flex items-center justify-center">
                            <span className="text-sm md:text-xs text-gray-500 dark:text-gray-400 font-medium">
                                {notifications.length} notification{notifications.length !== 1 ? 's' : ''} displayed
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default NotificationMenu;
